import React, {useEffect, useState} from "react";
import {Box, Button, FormHelperText, Typography} from "@material-ui/core";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import FlexBox from "view/components/Layout/FlexBox";
import {RawAttachmentFile} from "ts/api/attachmentFile";
import {useForm} from "react-hook-form";
import Form from "view/components/Form";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ConfirmModal from "view/components/ConfirmModal";
import {
  defaultSignatureText,
  DiscardModalDetails,
  isEmptySignatureText,
  SinatureFormData,
  TabMap,
} from "view/apps/Organiser/pages/Show/Settings/ElectronicSignature/constants";
import {SignatureType} from "@evennt-tech/api-v2-core";
import {useStyles} from "./styles";
import ExistingSignaturesContainer from "./ExistingSignaturesContainer";
import NewSignaturesContainer from "./NewSignaturesContainer";
import {
  ICreateSignaturePublicReq,
  SignTaskModalProps,
  ViewModeEnum,
  ViewModes,
} from "./types";

const defaultModesValue: ViewModes = {
  saved: true,
  new: true,
};

const SignTaskModal: React.FC<SignTaskModalProps> = ({
  showId,
  signatures,
  createSignature,
  onClose,
  taskTitle,
  onSubmit,
  viewModes = defaultModesValue,
}) => {
  const classes = useStyles();
  const isOnlyNewViewMode = viewModes.new && !viewModes.saved;
  const defaultViewMode = isOnlyNewViewMode
    ? ViewModeEnum.new
    : ViewModeEnum.saved;
  const [viewMode, setViewMode] = useState<ViewModeEnum>(defaultViewMode);
  const [selectedSignatureId, setSelectedSignatureId] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState(0);
  const [paintedFile, setPaintedFile] = useState<RawAttachmentFile | null>(
    null
  );
  const [uploadedFile, setUploadedFile] = useState<RawAttachmentFile | null>(
    null
  );
  const [typedFile, setTypedFile] = useState<RawAttachmentFile | null>(null);
  const [signatureText, setSignatureText] = useState(defaultSignatureText);
  const [saveSignature, setSaveSignature] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const form = useForm<SinatureFormData>({
    mode: "all",
  });
  const {handleSubmit, formState} = form;
  const {isDirty, isValid, isSubmitting} = formState;

  useEffect(() => {
    const option =
      !signatures.length || isOnlyNewViewMode
        ? ViewModeEnum.new
        : ViewModeEnum.saved;
    setViewMode(option);
  }, [signatures]);

  const handleSaveSignatureToShow = async (
    name: string,
    role: string,
    file: RawAttachmentFile,
    type: SignatureType,
    text?: string,
    saveSignature?: boolean
  ): Promise<string | undefined> => {
    let signature: ICreateSignaturePublicReq;
    if (type === SignatureType.TEXT) {
      signature = {
        showId,
        name,
        role,
        type,
        file,
        text: text || "",
        shouldBeShownInList: !!saveSignature,
      };
    } else {
      signature = {
        showId,
        name,
        role,
        type,
        file,
        shouldBeShownInList: !!saveSignature,
      };
    }

    return (await createSignature(signature))?.id;
  };
  const handleSaveAndSubmit = async (
    values: SinatureFormData,
    file: RawAttachmentFile,
    type: SignatureType,
    text?: string,
    saveSignature?: boolean
  ) => {
    try {
      setError(null);
      const newSignatureId = await handleSaveSignatureToShow(
        values.name,
        values.role,
        file,
        type,
        text,
        saveSignature
      );
      onSubmit(newSignatureId ?? undefined);
      onClose();
    } catch (error) {
      console.error("Error saving or fetching signature:", error);
      setError(error.message);

      return null;
    }
  };
  const handleModalSubmit = async () => {
    if (viewMode === "saved" && selectedSignatureId) {
      onSubmit(selectedSignatureId);
      onClose();
      return;
    }

    if (viewMode === "new") {
      const values = form.getValues();
      const areFieldsFilled = !!(values.name?.trim() && values.role?.trim());

      if (!areFieldsFilled) {
        return;
      }

      if (activeTab === 0 && paintedFile) {
        return handleSaveAndSubmit(
          values,
          paintedFile,
          SignatureType.DRAWING,
          undefined,
          saveSignature
        );
      }
      if (activeTab === 1 && uploadedFile) {
        return handleSaveAndSubmit(
          values,
          uploadedFile,
          SignatureType.IMAGE,
          undefined,
          saveSignature
        );
      }
      if (activeTab === 2 && typedFile) {
        return handleSaveAndSubmit(
          values,
          typedFile,
          SignatureType.TEXT,
          signatureText,
          saveSignature
        );
      }
    }
  };
  const handleModalClose = () => {
    const hasFiles = !!paintedFile || !!typedFile || !!uploadedFile;

    return isDirty || hasFiles || selectedSignatureId
      ? setIsDiscardModalOpen(true)
      : onClose();
  };

  const tabMap: TabMap = {
    0: {type: SignatureType.DRAWING, file: paintedFile},
    1: {type: SignatureType.IMAGE, file: uploadedFile},
    2: {type: SignatureType.TEXT, file: typedFile},
  } as const;
  const disableSubmit = (() => {
    if (!isDirty || !isValid || isSubmitting) {
      return true;
    }

    const values = form.getValues();
    const areFieldsFilled = !!(values.name?.trim() && values.role?.trim());

    const currentTab = tabMap[activeTab as keyof typeof tabMap];
    const isFileMissed = !currentTab.file;
    const isSignatureTextInvalid =
      currentTab.type === SignatureType.TEXT &&
      isEmptySignatureText(signatureText, defaultSignatureText);

    return !areFieldsFilled || isFileMissed || isSignatureTextInvalid;
  })();
  const isSubmitDisabled =
    viewMode === "saved" ? !selectedSignatureId : disableSubmit;

  return (
    <>
      <FfairModal open onClose={onClose} isCalledByAnotherModal>
        <FfairPaper className={classes.root}>
          <Typography variant="h4" className={classes.taskTitle}>
            {taskTitle}
          </Typography>
          <Typography className={classes.title}>Sign the Task</Typography>
          <Form
            form={form}
            onSubmit={handleSubmit(handleModalSubmit)}
            name="edit-modal-form"
            className={classes.editModal}
          >
            {viewModes.saved && (
              <ExistingSignaturesContainer
                signatures={signatures}
                selectedOption={viewMode}
                setSelectedOption={setViewMode}
                selectedSignatureId={selectedSignatureId}
                setSelectedSignatureId={setSelectedSignatureId}
                signatureOptionContainerClass={classes.signatureOptionContainer}
                radioGroupClass={classes.radioGroup}
                labelTextClass={classes.labelText}
              />
            )}
            {viewModes.new && (
              <NewSignaturesContainer
                selectedOption={viewMode}
                setSelectedOption={setViewMode}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                setPaintedFile={setPaintedFile}
                uploadedFile={uploadedFile}
                setUploadedFile={setUploadedFile}
                setTypedFile={setTypedFile}
                signatureText={signatureText}
                setSignatureText={setSignatureText}
                saveSignature={saveSignature}
                setSaveSignature={setSaveSignature}
                signatureOptionContainerClass={classes.signatureOptionContainer}
                radioGroupClass={classes.radioGroup}
                labelTextClass={classes.labelText}
                isOnlyNewViewMode={isOnlyNewViewMode}
              />
            )}
            {error && (
              <Box mb={0.5}>
                <FormHelperText error>{error}</FormHelperText>
              </Box>
            )}
            <FlexBox style={{marginTop: "16px"}} justifyContent="space-between">
              <FlexBox
                alignCenter
                onClick={onClose}
                style={{cursor: "pointer"}}
              >
                <ArrowBackIcon color="primary" />
                <Button color="primary">Back</Button>
              </FlexBox>
              <Box>
                <Button
                  onClick={handleModalClose}
                  color="primary"
                  style={{marginRight: "16px"}}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitDisabled}
                >
                  Submit
                </Button>
              </Box>
            </FlexBox>
          </Form>
        </FfairPaper>
      </FfairModal>
      {isDiscardModalOpen && (
        <ConfirmModal
          title={DiscardModalDetails.title}
          subtitle={DiscardModalDetails.subtitle}
          submitTitle={DiscardModalDetails.submitTitle}
          description={DiscardModalDetails.description}
          submitColor="primary"
          submitVariant="contained"
          severity="info"
          onClose={() => setIsDiscardModalOpen(false)}
          onSubmit={() => {
            setIsDiscardModalOpen(false);
            onClose();
          }}
        />
      )}{" "}
    </>
  );
};

export default SignTaskModal;
